<template>
  <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onSubmit">
    <v-alert v-if="overlapError" :value="true" color="error" icon="warning" class="mb-5" dark>{{
      $t('message.' + overlapError + '_overlap_error')
    }}</v-alert>

    <v-overlay
      :value="periodLoading || paymentLoading || stripeLoading || checkingDone || isLoadingAgreementPaymentMethodUpdate"
      :dark="false"
      color="white"
      opacity="0.90"
    >
      <div class="text-center pa-5">
        <v-progress-circular indeterminate color="primary" class="mb-4"></v-progress-circular>
        <div>{{ $t('message.request_being_processed') }}</div>
      </div>
    </v-overlay>

    <v-alert v-if="periodCreateError" :value="true" color="error" icon="warning" class="mb-5" dark>{{
      periodCreateError
    }}</v-alert>
    <div v-if="$vuetify.breakpoint.smAndDown" class="title">
      {{ $t('message.payment') }}
    </div>

    <v-row justify="center">
      <template v-if="'fixed_term' === agreement.servicePlan.type">
        <v-col sm="10" md="8" lg="7" xl="5">
          <p v-if="item.airtimePlan.defaultPrice.feesCount > 0">{{ $t('message.fixed_term_payment_paragraph_1') }}</p>
          <v-card outlined class="mb-6">
            <template v-if="!newPaymentMethod">
              <v-list two-line>
                <v-list-item>
                  <v-list-item-avatar>
                    <v-img
                      :src="
                        require('@/assets/img/payment_methods/' +
                          stripePaymentMethod.card.brand.toLowerCase().replace(/ /g, '-') +
                          '.svg')
                      "
                      alt="Credit Card Icons"
                    />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ getCardLabel(stripePaymentMethod.card) }}
                      <span v-if="stripePaymentMethod.billing_details.name" class="grey--text text--darken-2"
                        >({{ stripePaymentMethod.billing_details.name }})</span
                      >
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ $t('message.expires_in') }}

                      {{ $moment.months(stripePaymentMethod.card.exp_month - 1) }}
                      <template v-if="'es' === $i18n.locale">de</template>
                      {{ stripePaymentMethod.card.exp_year }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-card-actions>
                <v-btn text color="primary" @click="newPaymentMethod = true">{{
                  $t('message.change_or_add_card')
                }}</v-btn>
              </v-card-actions>
            </template>

            <template v-else>
              <PaymentMethodSelector :agreement="agreement" @change="stripePaymentMethod = $event" />
              <v-card-actions v-if="agreement.stripeSource">
                <v-btn text color="primary" @click="useDefaultPaymentMethod">{{
                  $t('message.use_default_card')
                }}</v-btn>
              </v-card-actions>
            </template>
          </v-card>

          <v-alert v-if="item.airtimePlan.defaultPrice.feesCount > 0" outlined type="info">
            {{ $t('message.fixed_term_payment_paragraph_2') }}
          </v-alert>

          <v-row justify="center">
            <v-btn class="ma-3" @click="$emit('updateStep', false === hasInitialAirtimePlan ? 3 : 2)">{{
              $t('message.back')
            }}</v-btn>
            <v-btn :disabled="!stripePaymentMethod" color="orange" class="white--text ma-3" type="submit"
              >{{ $t('message.pay') }}
              {{ $n(item.airtimePlan.defaultPrice.initialCharge.grossPrice, agreement.currency.code) }}</v-btn
            >
          </v-row>
        </v-col>
      </template>
      <template v-else>
        <v-col sm="6" md="5" lg="4" xl="3">
          <v-radio-group v-model="paymentMethod" :mandatory="false">
            <template v-if="!hasInitialAirtimePlan">
              <v-radio :label="$t('message.credit_card')" value="credit_card_online"></v-radio>
              <v-radio
                :label="$t('message.bank_transfer')"
                value="transfer"
                :disabled="false === transferEnoughAnticipation"
              ></v-radio>
              <div
                v-if="false === transferEnoughAnticipation"
                class="caption grey--text mb-2"
                style="margin-left: 35px"
              >
                {{
                  $t('message.transfer_anticipation_info', {
                    anticipation: transferAnticipation
                  })
                }}
              </div>
              <v-radio
                v-if="agreement && agreement.legalEntity.iban && directDebitAllowed"
                :label="$t('message.direct_debit')"
                value="direct_debit"
                :disabled="false === directDebitEnoughAnticipation"
              ></v-radio>
              <div
                v-if="false === directDebitEnoughAnticipation && directDebitAllowed"
                class="caption grey--text"
                style="margin-left: 35px"
              >
                {{
                  $t('message.direct_debit_anticipation_info', {
                    anticipation: directDebitAnticipation
                  })
                }}
              </div>
            </template>

            <template v-if="item.airtimePlan && !isBalanceExceeded(item.airtimePlan.balance)">
              <v-radio :label="$t('message.balance')" value="airtime_balance"></v-radio>
              <div class="caption mb-2" style="margin-left: 35px">
                {{
                  $t('message.remaining_balance_days_info', {
                    remainingDays: item.airtimePlan.balance.remainingDays,
                    spendDays:
                      $moment(item.endsAt)
                        .subtract(1, 'days')
                        .diff($moment(item.startsAt), 'days') + 1
                  })
                }}
              </div>
            </template>
          </v-radio-group>
          <template v-if="'credit_card_online' === paymentMethod">
            <div
              class="grey lighten-5 pa-4 mb-3"
              :class="{
                complete: cardComplete,
                'card-error': cardError,
                focus: cardFocus
              }"
            >
              <span class="font-weight-medium text-uppercase">{{ $t('message.card_details') }}</span>

              <v-text-field
                v-model="cardHolderName"
                :label="$t('message.card_holder_name')"
                required
                :rules="cardHolderNameRules"
              ></v-text-field>
              <CardElement
                ref="cardElement"
                :data="stripeData"
                @change="cardComplete = $event.complete && cardHolderName"
                @focus="cardFocus = true"
                @blur="cardFocus = false"
                @success="cardPaymentSuccess"
                @error="cardPaymentError"
                @loading="setStripeLoading"
              ></CardElement>
            </div>
            <span v-if="cardError" class="red--text">{{ cardError }}</span>

            <v-row justify="start" style="max-width: 400px">
              <v-alert v-if="periodCreateError && !periodViolations" :value="true" type="error" dismissible dark>{{
                $t('message.period_create.pay_error')
              }}</v-alert>
            </v-row>

            <v-row justify="center">
              <v-btn class="ma-3" @click="$emit('updateStep', false === hasInitialAirtimePlan ? 3 : 2)">{{
                $t('message.back')
              }}</v-btn>
              <v-btn
                :disabled="!cardComplete || periodLoading || paymentLoading || stripeLoading || checkingDone"
                color="orange"
                class="white--text ma-3"
                type="submit"
                >{{ $t('message.pay') }}</v-btn
              >
            </v-row>
          </template>

          <template v-if="'transfer' === paymentMethod">
            <div class="grey lighten-5 pa-4 mb-3">
              {{ $t('message.bank_transfer_info') }}
            </div>

            <v-row justify="center">
              <v-btn class="ma-3" @click="$emit('updateStep', false === hasInitialAirtimePlan ? 3 : 2)">{{
                $t('message.back')
              }}</v-btn>
              <v-btn
                :disabled="periodLoading || paymentLoading || stripeLoading"
                color="orange"
                class="white--text ma-3"
                type="submit"
              >
                <template v-if="$vuetify.breakpoint.xsOnly">{{ $t('message.request') }}</template>
                <template v-else>{{ $t('message.request_activation') }}</template>
              </v-btn>
            </v-row>
          </template>

          <template v-if="'direct_debit' === paymentMethod">
            <div
              class="grey lighten-5 pa-4 mb-3"
              v-html="
                $t('message.direct_debit_iban_info', {
                  iban: ofuscateIban(agreement.legalEntity.iban)
                })
              "
            ></div>
            <v-row justify="center">
              <v-btn class="ma-3" @click="$emit('updateStep', false === hasInitialAirtimePlan ? 3 : 2)">{{
                $t('message.back')
              }}</v-btn>
              <v-btn
                :disabled="periodLoading || paymentLoading || stripeLoading || checkingDone"
                color="orange"
                class="white--text ma-3"
                type="submit"
              >
                <template v-if="$vuetify.breakpoint.xsOnly">{{ $t('message.request') }}</template>
                <template v-else>{{ $t('message.request_activation') }}</template>
              </v-btn>
            </v-row>
          </template>

          <template v-if="'airtime_balance' === paymentMethod">
            <div class="grey lighten-5 pa-4 mb-3">
              Vas a utilizar
              {{
                $moment(item.endsAt)
                  .subtract(1, 'days')
                  .diff($moment(item.startsAt), 'days') + 1
              }}
              días de tu saldo. Tras la contratación tendrás disponibles
              {{
                item.airtimePlan.balance.remainingDays -
                  ($moment(item.endsAt)
                    .subtract(1, 'days')
                    .diff($moment(item.startsAt), 'days') +
                    1)
              }}
              días.
            </div>
            <v-row justify="center">
              <v-btn class="ma-3" @click="$emit('updateStep', false === hasInitialAirtimePlan ? 3 : 2)">{{
                $t('message.back')
              }}</v-btn>
              <v-btn
                :disabled="periodLoading || paymentLoading || stripeLoading || checkingDone"
                color="orange"
                class="white--text ma-3"
                type="submit"
              >
                <template v-if="$vuetify.breakpoint.xsOnly">{{ $t('message.request') }}</template>
                <template v-else>{{ $t('message.request_activation') }}</template>
              </v-btn>
            </v-row>
          </template>
        </v-col>
      </template>
    </v-row>
  </v-form>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import CardElement from '@/components/CardElement'
import { ofuscateIban } from '@/utils/string'
import fetch from '@/utils/fetch'
import PaymentMethodSelector from '@/components/ActivationCreateDialog/PaymentMethodSelector'

export default {
  name: 'Payment',

  components: { PaymentMethodSelector, CardElement },

  props: {
    agreement: {
      type: Object,
      required: true
    },
    item: {
      type: Object,
      required: true
    },
    airtimeBalancesMerged: {
      type: Array,
      required: false,
      default: null
    },
    hasInitialAirtimePlan: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      valid: true,
      cardComplete: false,
      cardError: null,
      cardFocus: false,
      paymentMethod: this.hasInitialAirtimePlan ? 'airtime_balance' : 'credit_card_online',
      stripeLoading: false,
      cardHolderName: null,
      cardHolderNameRules: [
        v => {
          return (!!v && !!v.trim()) || this.$t('message.card_holder_name_is_required')
        }
      ],
      paymentDoneCheckInternval: null,
      checkingDone: false,
      ofuscateIban: ofuscateIban,
      overlapError: null,
      stripePaymentMethod: this.agreement.stripeSource,
      newPaymentMethod: false,
      isLoadingAgreementPaymentMethodUpdate: false
    }
  },

  computed: {
    ...mapGetters({
      periodCreated: 'serviceperiod/create/created',
      periodLoading: 'serviceperiod/create/isLoading',
      periodCreateError: 'serviceperiod/create/error',
      periodViolations: 'serviceperiod/create/violations',
      paymentCreated: 'payment/create/created',
      paymentLoading: 'payment/create/isLoading',
      paymentCreateError: 'payment/create/error',
      paymentViolations: 'payment/create/violations',
      settingsError: 'setting/list/error',
      settings: 'setting/list/items',
      isLoadingSettings: 'setting/list/isLoading'
    }),

    stripeData() {
      if (this.agreement && this.cardHolderName) {
        let data = {
          payment_method_data: {
            billing_details: {
              name: this.cardHolderName,
              email: this.agreement.legalEntity.email,
              address: {
                city: this.agreement.legalEntity.city,
                country: this.agreement.legalEntity.country,
                line1: this.agreement.legalEntity.address,
                state: this.agreement.legalEntity.region
              },
              phone: this.agreement.legalEntity.phone
            }
          },
          receipt_email: this.agreement.legalEntity.email
        }

        if (this.agreement.legalEntity.postalCode) {
          data.payment_method_data.billing_details.address.postal_code = this.agreement.legalEntity.postalCode
        }

        return data
      }

      return null
    },

    directDebitAllowed() {
      if (this.agreement) {
        return this.agreement.legalEntity.directDebitAllowed
      }

      return false
    },

    directDebitAnticipation() {
      let anticipation = null

      if (this.settings) {
        this.settings.forEach(setting => {
          if (setting.name === 'payment_anticipation_direct_debit') {
            anticipation = setting.currentValue || setting.defaultValue
          }
        })
      }

      return anticipation
    },

    transferAnticipation() {
      let anticipation = null

      if (this.settings) {
        this.settings.forEach(setting => {
          if (setting.name === 'payment_anticipation_transfer') {
            anticipation = setting.currentValue || setting.defaultValue
          }
        })
      }

      return anticipation
    },

    directDebitEnoughAnticipation() {
      if (this.directDebitAnticipation && this.item.startsAt) {
        const diffDays = this.$moment(this.item.startsAt).diff(this.$moment(), 'days')
        return diffDays >= this.directDebitAnticipation
      }

      return false
    },

    transferEnoughAnticipation() {
      if (this.transferAnticipation && this.item.startsAt) {
        const diffDays = this.$moment(this.item.startsAt).diff(this.$moment(), 'days')
        return diffDays >= this.transferAnticipation
      }

      return false
    }
  },

  watch: {
    periodCreated: function(periodCreated) {
      if (!periodCreated) {
        return
      }

      if ('activation' === periodCreated.type) {
        if ('fixed_term' === this.agreement.servicePlan.type) {
          this.$emit('periodCreated', periodCreated)
        } else if (this.paymentCreated) {
          this.$refs.cardElement.handleCardPayment(this.paymentCreated.clientSecret)
        } else {
          let payment = {}
          payment.status = 'pending'
          payment.type = this.paymentMethod
          payment.parentOrder = periodCreated.orderLines[0].parentOrder['@id']
          this.createPayment(payment)
        }
      } else {
        this.$emit('periodCreated', periodCreated)
        this.resetPeriod()
        this.resetPayment()
      }
    },

    paymentCreated: function(paymentCreated) {
      if (!paymentCreated) {
        return
      }

      if (paymentCreated.type === 'credit_card_online') {
        this.$refs.cardElement.handleCardPayment(paymentCreated.clientSecret)
      } else {
        this.$emit('paymentCreated', paymentCreated)
        this.resetPeriod()
        this.resetPayment()
      }
    },

    periodViolations: function(periodViolations) {
      if (periodViolations && 'date' in periodViolations && periodViolations.date.search('overlaps') > 0) {
        this.overlapError = 'activation'
      }
    }
  },

  created() {
    if (!this.stripePaymentMethod) {
      this.newPaymentMethod = true
    }
  },

  mounted() {
    this.getSettings('/settings?pagination=false')
  },

  methods: {
    ...mapActions({
      createPeriod: 'serviceperiod/create/create',
      resetPeriod: 'serviceperiod/create/reset',
      createPayment: 'payment/create/create',
      resetPayment: 'payment/create/reset',
      getSettings: 'setting/list/default',
      settingsReset: 'setting/list/reset'
    }),

    useDefaultPaymentMethod() {
      this.newPaymentMethod = false
      this.stripePaymentMethod = this.agreement.stripeSource
    },

    isBalanceExceeded(airtimeBalance) {
      if (!airtimeBalance) {
        return true
      }
      return (
        this.$moment(this.item.endsAt)
          .subtract(1, 'days')
          .diff(this.$moment(this.item.startsAt), 'days') >= airtimeBalance.remainingDays
      )
    },

    cardPaymentSuccess() {
      const _this = this
      let attempts = 10
      let attempt = 1
      this.checkingDone = true
      this.paymentDoneCheckInternval = setInterval(() => {
        fetch(_this.paymentCreated ? _this.paymentCreated['@id'] : _this.relevantPayment['@id'])
          .then(response => response.json())
          .then(data => {
            if (data.status === 'done') {
              clearInterval(this.paymentDoneCheckInternval)
              _this.$emit('paymentCreated', this.paymentCreated)
              _this.resetPayment()
              _this.resetPeriod()
              this.checkingDone = false
            }
          })
          .catch(e => {
            // eslint-disable-next-line no-console
            console.log(e)
            this.checkingDone = false
          })
          .then(() => {
            if (attempt >= attempts) {
              this.checkingDone = false
              clearInterval(this.paymentDoneCheckInternval)
              _this.cardError = 'Your payment was success but your service can not be activated. Please contact us.'
            }
            attempt = attempt + 1
          })
      }, 3000)
    },

    cardPaymentError(error) {
      this.cardError = error.message
      this.$emit('cardError')
    },

    setStripeLoading(value) {
      this.stripeLoading = value
    },

    onSubmit() {
      if (!this.periodCreated) {
        if (this.$refs.form.validate()) {
          let cleanItem = { ...this.item }

          if (cleanItem.startsAt) {
            cleanItem.startsAt = this.$moment(cleanItem.startsAt)
              .tz('Europe/Madrid')
              .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
              .toISOString()
          }

          if (cleanItem.endsAt) {
            if ('day' === this.agreement.servicePlan.granularity) {
              cleanItem.endsAt = this.$moment(cleanItem.endsAt)
                //.add(1, 'days')
                .tz('Europe/Madrid')
                .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
                .toISOString()
            } else {
              cleanItem.endsAt = this.$moment(cleanItem.endsAt)
                .tz('Europe/Madrid')
                .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
                .toISOString()
            }
          }

          if (cleanItem.airtimePlan && 'string' !== typeof cleanItem.airtimePlan) {
            cleanItem.airtimePlan = cleanItem.airtimePlan['@id']
          }

          if (cleanItem.orderLines && cleanItem.orderLines.length > 0) {
            cleanItem.orderLines.forEach((orderLine, index) => {
              if ('string' !== typeof orderLine) {
                cleanItem.orderLines[index] = orderLine['@id']
              }
            })
          }

          if (
            'fixed_term' === this.agreement.servicePlan.type &&
            (!this.agreement.stripeSource || this.stripePaymentMethod.id !== this.agreement.stripeSource.id)
          ) {
            this.isLoadingAgreementPaymentMethodUpdate = true
            fetch(this.agreement['@id'], {
              method: 'PUT',
              headers: new Headers({ 'Content-Type': 'application/ld+json' }),
              body: JSON.stringify({ stripeSourceId: this.stripePaymentMethod.id })
            })
              .then(response => response.json())
              .then(() => {
                this.createPeriod(cleanItem)
                this.isLoadingAgreementPaymentMethodUpdate = false
              })
              .catch(e => {
                // eslint-disable-next-line no-console
                console.log(e)
                this.isLoadingAgreementPaymentMethodUpdate = false
              })
          } else {
            this.createPeriod(cleanItem)
          }
        }
      } else {
        if ('activation' === this.periodCreated.type && 'fixed_term' !== this.agreement.servicePlan.type) {
          if (this.paymentCreated) {
            this.$refs.cardElement.handleCardPayment(this.paymentCreated.clientSecret)
          } else {
            let payment = {}
            payment.status = 'pending'
            payment.type = this.paymentMethod
            payment.parentOrder = this.periodCreated.orderLines[0].parentOrder['@id']
            this.createPayment(payment)
          }
        } else {
          this.resetPayment()
          this.resetPeriod()
        }
      }
    },
    getCardLabel(card) {
      return this.$t('message.payment_methods.brand.' + card.brand) + ' •••• ' + card.last4
    }
  }
}
</script>

<style lang="stylus" scoped>
.card-error
    >>>.fake-material-input
        background rgba(255, 0, 0, 0.1)

        .input-effect
            background-color #E15141

.stripe-card
    max-width 400px
    width 100%

.stripe-card.complete
    border-color green
</style>
